<template>
  <div v-if="puzzle" class="puzzle-wrapper">
    <div class="puzzle">
      <div @touchend="endTurn" @mouseup="endTurn">
        <div v-for="(letters, row) in puzzle" :key="row">
          <span
            class="letter"
            :data-row="row"
            :data-col="colindex"
            :class="fieldClass(row, colindex)"
            v-for="(char, colindex) in letters"
            :key="key(row, colindex)"
            @mousedown="startTurn(row, colindex)"
            @mouseenter="mouseMove(row, colindex)"
            @touchstart="startTurn(row, colindex)"
            @touchmove="touchMove"
            >
            {{char}}
          </span>
        </div>
      </div>
    </div>
    <div class="found-words">
      <span class="has-text-primary">{{foundWords.length}}</span>
      av
      {{wordlist.length}}
      ord funna
    </div>
    <button class="side-button button is-success" @click="showConfirmation = true">
      K<br>L<br>A<br>R
    </button>
    <button class="side-button button is-danger" @click="showHelpModal = true">
      H<br>J<br>Ä<br>L<br>P
    </button>
    <confirm
      message="Är du säker på att du vill skicka in svaren?"
      v-if="showConfirmation"
      @confirm="submit"
      @cancel="showConfirmation = false"
    />
    <div class="introbox-overlay" v-if="showHelpModal">
      <intro-box>
        <div class="text-content">
          <div v-html="task.description" />
        </div>
        <div class="introduction-spacer">

        </div>
        <div class="bottom_align">
          <button type="button" class="button is-primary is-rounded" @click="showHelpModal = false">
            Jag förstår, nu kör vi!
          </button>
        </div>
      </intro-box>
    </div>
  </div>
</template>

<script>
import { generatePuzzle, words } from './hardcoded'
import { generatePuzzle_3d, words_3d } from './hardcoded_3d'
import WordFind from './WordFind'
import Confirm from '@/components/elements/Confirm/Confirm.vue'
import IntroBox from '@/components/elements/IntroBox.vue'

function parseKey(key) {
  let x
  let y
  [y, x] = key.split('-')
  return {x: parseInt(x), y: parseInt(y)}
}

function calcOrientation(fromKey, toKey) {
  let from = parseKey(fromKey)
  let to = parseKey(toKey)

  for (let orientation in WordFind.orientations) {
    let nextFn = WordFind.orientations[orientation]
    let nextPos = nextFn(from.x, from.y, 1)

    if (nextPos.x === to.x && nextPos.y === to.y) {
      return orientation
    }
  }

  return null
}

export default {
  data () {
    return {
      puzzle: null,
      selected: [],
      currentWord: null,
      currentOrientation: null,
      startSquare: null,
      found: [],
      foundWords: [],
      showConfirmation: false,
      showHelpModal: false
    }
  },
  computed: {
    task() {
      return this.$store.state.assignment.task
    },
    wordlist(){
      if(this.task.scene_number){
        // video task, load 3d puzzle words
        return words_3d.map(w => w.toLowerCase())
      }else{
        // normal task
        return words.map(w => w.toLowerCase())
      }
    }
  },
  methods: {
    key(row, col) {
      return `${row}-${col}`
    },

    fieldClass(row, col) {
      let key = this.key(row, col)
      let isSelected = this.selected.includes(key)
      let isFound = this.found.includes(key)
      return {selected: isSelected, found: isFound}
    },

    startTurn(row, colindex) {
      this.startSquare = this.key(row, colindex)
      this.selected = []
      this.selected.push(this.key(row, colindex))
      this.currentWord = this.puzzle[row][colindex]
    },

    touchMove(event) {
      let xPos = event.touches[0].pageX
      let yPos = event.touches[0].pageY
      let target = document.elementFromPoint(xPos, yPos)
      if (target.tagName == 'SPAN') {
        if (target.dataset.hasOwnProperty('col')) {
          this.mouseMove(target.dataset.row, target.dataset.col)
        }
      }
    },

    mouseMove(row, colindex) {
      if (!this.startSquare) {
        return
      }
      const key = this.key(row, colindex)
      // if the new one is the last selected one, return
      let lastSquare = this.selected[this.selected.length - 1]

      if (lastSquare == key) {
        return
      }

      // see if the user backed up and correct our word
      let back
      for (let i = 0, len = this.selected.length; i < len; i++) {
        if (this.selected[i] == key) {
          back = i + 1
          break
        }
      }

      while (back < this.selected.length) {
        this.selected.splice(back, 1)
        this.currentWord = this.currentWord.substr(0, this.currentWord - 1)
      }

      let newOrientation = calcOrientation(this.startSquare, key)

      if (newOrientation) {
        this.selected = [this.startSquare]
        let start = parseKey(this.startSquare)
        this.currentWord = this.puzzle[start.y][start.x]
        this.currentOrientation = newOrientation
        if (lastSquare != this.startSquare) {
          lastSquare = this.startSquare
        }
      }

      let orientation = calcOrientation(lastSquare, key)
      if (!orientation) {
        return
      }

      if (!this.currentOrientation || this.currentOrientation == orientation) {
        this.currentOrientation = orientation
        this.playTurn(row, colindex)
      }
    },

    playTurn(row, colindex) {
      for (let i = 0, len = this.wordlist.length; i < len; i++) {
        if (this.wordlist[i].indexOf(this.currentWord + this.puzzle[row][colindex]) === 0) {
          this.selected.push(this.key(row, colindex))
          this.currentWord += this.puzzle[row][colindex]
          break;
        }
      }
    },

    endTurn() {
      for (let i = 0, len = this.wordlist.length; i < len; i++) {
        if (this.wordlist[i] === this.currentWord) {
          this.foundWords.push(this.currentWord)
          this.found.push(...this.selected)
        }
      }
      this.selected = []
      this.currentWord = null
      this.startSquare = null
      this.currentOrientation = null
    },

    submit() {
      this.showConfirmation = false
      let submission = {}
      submission.words = this.wordlist.map(w => {
        let found = this.foundWords.includes(w)
        return {word: w, found: found}
      })
      this.$store.commit('setSubmission', submission)
      this.$store.dispatch('submitAssignment', true)
      .then(() => {
        this.$router.push(`/assignment/${this.$store.state.assignment.slug}/confirmation`)
      })
    }
  },
  components: {
    Confirm,
    IntroBox
  },
  beforeCreate () {
    document.body.addEventListener('mouseup', this.endTurn)
    document.body.addEventListener('touchend', this.endTurn)
  },
  beforeDestroy() {
    document.body.removeEventListener('mouseup', this.endTurn)
    document.body.removeEventListener('touchend', this.endTurn)
  },

  mounted () {
    if(this.task.scene_number){
      this.puzzle = generatePuzzle_3d()
    }else{
      this.puzzle = generatePuzzle()
    }

  }
}
</script>

<style lang="scss" scoped>
@import "../../../assets/scss/variables";
.puzzle-wrapper {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-direction: column;
  background: #E9E9EA;
  overflow-y: scroll;
}
.puzzle {
  display: flex;
  flex: 1;
  justify-content: center;
  align-items: center;
}
.found-words {
  font-weight: bold;
  color: $grey-lighter;
  font-size: 12px;
}
.side-button {
  word-wrap: break-word;
  position: absolute;
  height: auto;
  top: 50%;
  transform: translateY(-50%);

  &.is-success {
    right: 0;
    border-top-right-radius: 0;
    border-bottom-right-radius: 0;
  }

  &.is-danger {
    left: 0;
    border-top-left-radius: 0;
    border-bottom-left-radius: 0;
  }
}
span.letter {
  display: inline-block;
  width: 36px;
  padding: 0.25rem 0;
  box-shadow: 0 2px 5px rgba(0,0,0, 0.2);
  margin-left: 0.3rem;
  text-align: center;
  margin-bottom: 0.3rem;
  text-transform: uppercase;
  user-select: none;
  color: $dark;
  font-weight: bold;
  font-size: 12px;
  border-radius: 5px;
  background: #fff;

  &:first-child {
    margin-left: 0;
  }

  &.selected,
  &.found {
    background: $primary;
    color: white;
  }

  &.found:not(.selected) {
    box-shadow: none;
  }
}
.introbox-overlay {
  position: fixed;
  display: flex;
  padding: 4rem;
  justify-content: center;
  align-items: stretch;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(white, 0.5);

  .box {
    overflow-y: scroll;
    .content {
      display: table;
    }
  }
}
</style>
